import type { StatusRewardBenefitIdType } from './statusRewardData';

export type LockBadgeKind = 'coming-soon' | 'needs-status';
export type LockBadge = { kind: 'coming-soon' } | { kind: 'needs-status'; statusName: string } | null;
export type BadgeSurface = 'statuses' | 'rewardsGrid';

type BadgeVisibility = Record<BadgeSurface, Record<LockBadgeKind, boolean>>;
type BadgeVisibilityOverrides = Partial<Record<
  StatusRewardBenefitIdType,
  Partial<Record<BadgeSurface, Partial<Record<LockBadgeKind, boolean>>>>
>>;

const DEFAULT_BADGE_VISIBILITY: BadgeVisibility = {
  statuses: { 'coming-soon': true, 'needs-status': false },
  rewardsGrid: { 'coming-soon': true, 'needs-status': true },
};

// Override a badge for one benefit and surface, e.g.
// 'loss-insurance': { statuses: { 'coming-soon': false } }
export const BADGE_VISIBILITY_OVERRIDES: BadgeVisibilityOverrides = {
  'personal-slot-game': {
    statuses: { 'coming-soon': false },
    rewardsGrid: { 'coming-soon': false, 'needs-status': true },
  },
  'purchase-bonus': {
    statuses: { 'coming-soon': false },
    rewardsGrid: { 'needs-status': true, 'coming-soon': false },
  },
};

interface Params {
  id: StatusRewardBenefitIdType;
  availability: LockBadgeKind | 'available' | 'unavailable';
  statusName?: string;
}

export const getBenefitLockBadges = (
  { id, availability, statusName }: Params,
  overrides: BadgeVisibilityOverrides = BADGE_VISIBILITY_OVERRIDES,
): Record<BadgeSurface, LockBadge> => {
  const getBadge = (surface: BadgeSurface): LockBadge => {
    if (availability === 'available' || availability === 'unavailable') {
      return null;
    }

    if (availability === 'needs-status' && !statusName) {
      return null;
    }

    const isVisible = overrides[id]?.[surface]?.[availability] ?? DEFAULT_BADGE_VISIBILITY[surface][availability];
    if (!isVisible) {
      return null;
    }

    if (availability === 'coming-soon') {
      return { kind: 'coming-soon' };
    }

    return statusName ? { kind: 'needs-status', statusName } : null;
  };

  return { statuses: getBadge('statuses'), rewardsGrid: getBadge('rewardsGrid') };
};
